import { initWeather3D, getWeather3DLegend } from './weather-3d.js';
// Radar pioggia VMI v2 — port della v1 (api/rain-vmi-cartographic.php): tutti i
// frame precaricati come image source MapLibre a opacità 0, lo scrub cambia
// solo raster-opacity → istantaneo, zero fetch al cambio frame. Storico,
// osservazione corrente e forecast restano nella timeline condivisa.
import { NowcastTimeline } from '../ui/nowcast-timeline.js';
import { setUnipaRadarEnabled, setUnipaRadarReference } from './unipa.js';
import { state as stationsState } from './stations.js';
import { barSpecFromPalette } from '../ui/nowcast-legend.js';
import { enforceObservationLayerOrder } from '../lib/observation-layer-order.js';

function publishRadarLegend() {
    const volumeLegend=getWeather3DLegend();
    if(volumeLegend){document.dispatchEvent(new CustomEvent('wsv2:map-legend',{detail:{id:'radar',spec:volumeLegend}}));return;}
    if (state.product === 'vmi-world') {
        document.dispatchEvent(new CustomEvent('wsv2:map-legend', { detail: { id: 'radar', spec: {
            title: 'Radar pioggia',
            unit: 'dBZ',
            bar: WORLD_RADAR_PALETTE.map(([v, color]) => ({ pos: (v-15)/50, color })),
            ticks: [15, 25, 35, 45, 55, 65].map(v => ({ pos: (v-15)/50, label: String(v) })),
        } } }));
        return;
    }
    const vmi = state.unit !== 'mm/h';
    const title = state.product === 'satellite-rain' ? 'Pioggia stimata da satellite' : state.unit === 'mm/h' ? 'Radar · pioggia al suolo' : 'Radar · echi atmosferici';
    const spec = barSpecFromPalette(title, vmi ? 'dBZ' : 'mm/h', state.palette);
    if (spec) {
        const vmax = vmi ? 65 : Math.max(80, ...state.palette.map(p => Number(p.value)).filter(Number.isFinite));
        spec.ticks = (vmi ? [5, 20, 35, 50, 65] : vmax > 80 ? [0, 25, 80, vmax] : [0, 8, 25, 80]).map(v => ({ pos: v / vmax, label: String(v) }));
    }
    // La legenda descrive il layer, non la timeline: usa il contenitore generale
    // della mappa così resta visibile anche quando il toggle Timeline è spento.
    document.dispatchEvent(new CustomEvent('wsv2:map-legend', { detail: { id: 'radar', spec } }));
}

// RainViewer Universal Blue (scheme 2), official discrete reflectivity colors.
const WORLD_RADAR_PALETTE = [[15,'#88ddee'],[20,'#00a3e0'],[25,'#0077aa'],[30,'#005588'],
    [34,'#004768'],[35,'#ffee00'],[40,'#ffaa00'],[44,'#ff8100'],[45,'#ff4400'],
    [50,'#c10000'],[55,'#ffaaff'],[60,'#ff77ff'],[65,'#ffffff']];
const OPACITY = 0.95;           // 2026-07-03: alzato (era 0.88) per colori più vividi
// Il prodotto ufficiale cambia ogni ~5'. Un controllo leggero ogni minuto evita
// di aggiungere altri 2-3' di attesa al tempo di pubblicazione del DPC.
const REFRESH_MS = 60 * 1000;
const MOBILE_RADAR = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
const SHOW_VMI_FORECAST = true; // v1: inclusi salvo window.WS_RADAR_SHOW_VMI_FORECAST=false
const RADAR_API_BASE = 'https://api.weathersicily.it';
// I nomi restano quelli della cartografia di base: aggiungerli al centro
// dei confini comunali duplica e sposta le etichette rispetto agli abitati.

const state = {
    product: 'auto',
    unit: 'dBZ',
    activeProduct: 'vmi-national',
    fallback: false,
    fallbackReason: null,
    basePalette: null,
    map: null,
    frames: [],      // {timeMs, srcId, lyrId}
    enabled: false,
    timer: null,
    refreshPromise: null,
    refreshGeneration: 0,
    generation: 0,
    visibilityHandler: null,
    currentIdx: -1,
    baseTimeMs: null,
    frameSignature: '',
    beforeId: undefined, // il radar va SOTTO i simboli stazione
    boundsLL: null,      // [[latSW,lonSW],[latNE,lonNE]] per il campionamento
    _clickPopup: null,
};

// Native observed SRI is queried numerically; display colours are approximate.
// --- click → rain rate: reverse-palette for other products only ---------------
// Il valore mostrato è stimato dal COLORE del radar (palette value→rgba del
// motore, esposta nel manifest) → è una lettura indicativa, non il dato grezzo.
let _revLut = null;   // [{v,r,g,b}] campionato dalla palette
function buildRevLut(palette) {
    _revLut = null;
    if (!Array.isArray(palette) || palette.length < 2) return;
    const stops = palette
        .map(p => ({ v: Number(p.value), c: p.rgba }))
        .filter(s => Number.isFinite(s.v) && Array.isArray(s.c));
    if (stops.length < 2) return;
    const vmax = stops[stops.length - 1].v;
    const lut = [];
    const step = Math.max(0.05, vmax / 1400);
    for (let v = step; v <= vmax; v += step) {
        let seg = 0;
        for (let i = 0; i < stops.length - 1; i++) {
            if (v >= stops[i].v && (v < stops[i + 1].v || i === stops.length - 2)) { seg = i; break; }
        }
        const lo = stops[seg], hi = stops[seg + 1];
        const t = (v - lo.v) / Math.max(1e-4, hi.v - lo.v);
        lut.push({
            v,
            r: lo.c[0] + (hi.c[0] - lo.c[0]) * t,
            g: lo.c[1] + (hi.c[1] - lo.c[1]) * t,
            b: lo.c[2] + (hi.c[2] - lo.c[2]) * t,
        });
    }
    _revLut = lut;
}
function valueFromColor(r, g, b) {
    if (!_revLut) return null;
    let best = null, bd = Infinity;
    for (const e of _revLut) {
        const d = (e.r - r) ** 2 + (e.g - g) ** 2 + (e.b - b) ** 2;
        if (d < bd) { bd = d; best = e; }
    }
    return best ? best.v : null;
}
function normMerc(lat, lon) {
    const x = lon / 360 + 0.5;
    const s = Math.max(-0.9999, Math.min(0.9999, Math.sin(lat * Math.PI / 180)));
    const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
    return { x, y };
}
function sampleDisplayValue(lngLat) {
    return new Promise((resolve) => {
        const fr = state.frames[state.currentIdx];
        if (!fr || !state.boundsLL) { resolve(null); return; }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onerror = () => resolve(null);
        img.onload = () => {
            try {
                const W = img.naturalWidth, H = img.naturalHeight;
                const [[latSW, lonSW], [latNE, lonNE]] = fr.bounds || state.boundsLL;
                const mSW = normMerc(latSW, lonSW), mNE = normMerc(latNE, lonNE), mC = normMerc(lngLat.lat, lngLat.lng);
                const fx = (mC.x - mSW.x) / (mNE.x - mSW.x);
                const fy = (mC.y - mNE.y) / (mSW.y - mNE.y);
                if (fx < 0 || fx > 1 || fy < 0 || fy > 1) { resolve(null); return; }
                const px = Math.round(fx * (W - 1)), py = Math.round(fy * (H - 1));
                const cv = document.createElement('canvas');
                cv.width = 1; cv.height = 1;
                const ctx = cv.getContext('2d', { willReadFrequently: true });
                ctx.imageSmoothingEnabled = false;
                ctx.drawImage(img, px, py, 1, 1, 0, 0, 1, 1);
                const d = ctx.getImageData(0, 0, 1, 1).data;
                if (d[3] < 18) { resolve(null); return; } // trasparente: assenza o dato mancante
                resolve(valueFromColor(d[0], d[1], d[2]));
            } catch (e) { resolve(null); }
        };
        img.src = fr.url; // stesso origin → canvas non tainted
    });
}
async function sampleRainRate(lngLat) {
    const fr = state.frames[state.currentIdx];
    if (!fr) return null;
    if (fr.nativePointAvailable && fr.kind === 'observed') {
        try {
            const query = new URLSearchParams({frame:fr.filename, lat:String(lngLat.lat), lon:String(lngLat.lng)});
            const response = await fetch(`${RADAR_API_BASE}/v1/radar/point?${query}`, {
                cache:'no-store', signal:AbortSignal.timeout(6000),
            });
            const payload = await response.json(), d = payload?.data;
            if (!response.ok || !payload?.success) throw new Error('RADAR_POINT_UNAVAILABLE');
            if (d?.frame !== fr.filename || Date.parse(d.time_utc) !== fr.timeMs
                    || d.unit !== 'mm/h' || d.sampling !== 'native-cell') return null;
            // A missing native observation must never become a dry observation
            // or a rain value reconstructed from a smoothed picture.
            return d.available === true && Number.isFinite(d.value) && d.value >= 0
                ? {value:d.value, native:true} : null;
        } catch (_) {
            const value = await sampleDisplayValue(lngLat);
            return value === null ? null : {value, native:false};
        }
    }
    const value = await sampleDisplayValue(lngLat);
    return value === null ? null : {value, native:false};
}

function onRadarClick(e) {
    // Radar background is clickable; leave station hits to their own popup.
    if (!state.enabled || state.product === 'vmi-world') return;
    const markerLayers = ['ws-stations-symbols', 'ws-stations-rain-symbols'].filter(id => state.map.getLayer(id));
    if (markerLayers.length && state.map.queryRenderedFeatures(e.point, {layers:markerLayers}).length) return;
    const clickToken=state.clickToken=(state.clickToken||0)+1;
    if (state.weather3D?.handleClick(e)) return;
    const clickedFrame = state.frames[state.currentIdx], clickedUnit = state.unit;
    sampleRainRate(e.lngLat).then((reading) => {
        if (state.clickToken !== clickToken || state.frames[state.currentIdx] !== clickedFrame || state.unit !== clickedUnit) return;
        // Niente popup dove non c'è traccia radar (richiesta utente): mostro
        // solo se c'è pioggia effettiva (v>0), non un inutile "nessuna pioggia".
        if (!reading || reading.value <= 0 || !state.enabled) return;
        const v = reading.value;
        const txt = state.product === 'satellite-rain'
            ? `🛰️ Pioggia stimata da satellite<br><b style="font-size:16px">${v < 1 ? v.toFixed(1) : Math.round(v)} mm/h</b><br>Stima infrarossa, non misurazione radar`
            : state.unit !== 'mm/h'
            ? `🌧️ Eco radar ${state.frames[state.currentIdx]?.kind === 'forecast' ? (state.forecastMethod === 'eulerian-persistence' ? 'in persistenza · affidabilità ridotta' : 'previsto') : 'osservato'}<br><b style="font-size:16px">${Math.round(v)} dBZ</b><br>${state.fallback ? (state.fallbackReason === 'surface_quality' ? 'Stima al suolo da verificare: mostro gli echi radar.' : 'Pioggia al suolo non disponibile: mostro gli echi radar.') : 'Riflettività, non pioggia misurata al suolo'}`
            : `🌧️ ${state.frames[state.currentIdx]?.kind === 'forecast' ? 'Pioggia prevista · WS Now' : 'Pioggia al suolo stimata dal radar'}<br><b style="font-size:16px">${v < 1 ? v.toFixed(1) : Math.round(v)} mm/h</b>`;
        if (state._clickPopup) state._clickPopup.remove();
        state._clickPopup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, className: 'wsv2-radar-popup', maxWidth: '200px' })
            .setLngLat(e.lngLat)
            .setHTML(`<div style="font:600 13px system-ui,-apple-system,sans-serif;padding:3px 5px;line-height:1.35">${txt}<div style="font-weight:500;font-size:10px;color:#6b7a89;margin-top:3px">${new Date(state.frames[state.currentIdx]?.timeMs).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Rome'})} · ${state.unit === 'mm/h' ? 'Intensità stimata' : 'Riflettività radar'}</div></div>`)
            .addTo(state.map);
        const popup=state._clickPopup;
        const detail=document.createElement('div');
        detail.style.cssText='font:500 10px system-ui;color:#6b7a89;padding:0 5px 4px';
        detail.textContent=reading.native
            ? 'Dato SRI originale · cella radar 1 km · stima al suolo'
            : 'Valore indicativo ricavato dal colore';
        popup.getElement()?.querySelector('.maplibregl-popup-content')?.append(detail);
        fetch(`/api/icon-ruc.php?lat=${e.lngLat.lat}&lon=${e.lngLat.lng}`,{cache:'no-store'})
            .then(r=>r.ok?r.json():null).then(g=>{
                if(!g?.available||state._clickPopup!==popup||!popup.isOpen())return;
                const panel=document.createElement('div');panel.style.cssText='padding:6px 5px;border-top:1px solid #7894;font:12px system-ui;line-height:1.5';
                const heading=document.createElement('b');heading.textContent='Prossime ore · ICON RUC';panel.append(heading);
                for(const p of g.series.slice(0,3)){
                    const row=document.createElement('div');const hour=new Date(p.time).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Rome'});
                    row.textContent=`Fino alle ${hour}: ${p.precipitation_mm.toLocaleString('it-IT')} mm nell’ora · vento ${p.wind_kmh.toLocaleString('it-IT')} km/h`;panel.append(row);
                }
                const note=document.createElement('small');note.textContent='Previsione oraria di modello, distinta dal radar.';panel.append(note);popup.getElement()?.querySelector('.maplibregl-popup-content')?.append(panel);
            }).catch(()=>{});
    });
}

function parseUtcMs(s) {
    return Date.parse(String(s || ''));
}

function clearFrames() {
    setUnipaRadarReference(null);
    const map = state.map;
    for (const f of state.frames) {
        try { if (map.getLayer(f.lyrId)) map.removeLayer(f.lyrId); } catch (e) {}
        try { if (map.getSource(f.srcId)) map.removeSource(f.srcId); } catch (e) {}
    }
    state.frames = [];
    state.currentIdx = -1;
    state.frameSignature = '';
}

function setWorldRadarCoverage(tiles) {
    const map = state.map;
    const id = 'ws-radar-coverage';
    if (!tiles) {
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(id)) map.removeSource(id);
        return;
    }
    if (map.getSource(id)) return;
    map.addSource(id, {type:'raster', tiles:[tiles], tileSize:256, maxzoom:7});
    map.addLayer({id, type:'raster', source:id,
        paint:{'raster-opacity':.25,'raster-fade-duration':0}}, radarBeforeLayer(map));
}

// Keep only the current frame and its neighbours in GPU memory on every device.
function radarBeforeLayer(map) {
    return map.getStyle().layers.find(layer =>
        ['ws-blitz', 'ws-stations-symbols'].includes(layer.id))?.id || state.beforeId;
}

function ensureFrame(fr) {
    const map = state.map;
    if (map.getSource(fr.srcId)) return;
    const [[s,w],[n,e]] = fr.bounds || state.boundsLL;
    const before = radarBeforeLayer(map);
    map.addSource(fr.srcId, fr.tiles
        ? {type:'raster',tiles:[fr.tiles],tileSize:256,maxzoom:fr.maxzoom,attribution:'Radar © <a href="https://www.rainviewer.com/">RainViewer</a>'}
        : {type:'image',url:fr.url,coordinates:[[w,n],[e,n],[e,s],[w,s]]});
    map.addLayer({id:fr.lyrId,type:'raster',source:fr.srcId,
        paint:{'raster-opacity':0,'raster-fade-duration':0,'raster-resampling':'linear'}},before);
    enforceObservationLayerOrder(map);
}

function prepareVisibleFrames(index) {
    const keep = new Set([index-1,index,index+1]);
    state.frames.forEach((fr,i) => {
        if (keep.has(i)) ensureFrame(fr);
        else {
            if (state.map.getLayer(fr.lyrId)) state.map.removeLayer(fr.lyrId);
            if (state.map.getSource(fr.srcId)) state.map.removeSource(fr.srcId);
        }
    });
}

async function loadFrames(generation) {
    const map = state.map;
    const resp = await fetch(`${RADAR_API_BASE}/v1/radar/frames?product=${state.product}`, { cache: 'no-store' });
    const payload = await resp.json();
    const data = payload?.data;
    if (!resp.ok || !payload?.success || !Array.isArray(data?.frames)) throw new Error('RADAR_ERROR');
    if (!state.enabled || generation !== state.generation) return;
    const [[s, w], [n, e]] = data.bounds;
    const coords = [[w, n], [e, n], [e, s], [w, s]];
    if (state.unit !== (data.unit || 'dBZ') || state.fallback !== (data.fallback === true)) {
        state._clickPopup?.remove(); state._clickPopup = null;
    }
    state.unit = data.unit || 'dBZ';
    state.activeProduct = data.active_product || state.product;
    state.fallback = data.fallback === true;
    state.fallbackReason = data.fallback_reason || null;
    state.basePalette = data.palette || [];
    state.boundsLL = [[s, w], [n, e]];   // per il campionamento click→mm/h
    setWorldRadarCoverage(data.coverage_tiles);
    buildRevLut(data.palette);
    state.palette = data.palette || null;
    state.forecastAvailable = data.forecast_available === true;
    state.forecastMethod = data.forecast_method || null;
    state.forecastHorizon = Math.max(0, ...data.frames.filter(fr => fr.kind === 'forecast').map(fr => Number(fr.lead_minutes) || 0));
    state.sourceAvailable = data.stale !== true && data.frames.length > 0;
    state.warning = data.stale ? 'Dati non aggiornati'
        : state.fallback ? (state.fallbackReason === 'surface_quality'
            ? 'Pioggia al suolo da verificare: mostrati gli echi radar (dBZ).'
            : 'Pioggia al suolo non disponibile: mostrati gli echi radar (dBZ).')
        : (state.product === 'rain' ? (data.quantitative_warning || (data.forecast_available ? null : 'Stima della pioggia futura non disponibile')) : null);
    // Legenda intensità pioggia indipendente dalla timeline (spec costruita
    // dalla palette del motore → colori identici al radar). Ripubblicata a ogni
    // loadFrames (la palette potrebbe cambiare) finché il radar è attivo.
    if (state.enabled) publishRadarLegend();

    NowcastTimeline.setForecastHorizon?.('radar', state.forecastAvailable ? state.forecastHorizon : 0);
    const frames = data.frames
        .filter(fr => SHOW_VMI_FORECAST || fr?.kind !== 'forecast')
        .map((fr, i) => ({
            timeMs: parseUtcMs(fr.time_utc),
            filename: fr.filename || null,
            nativePointAvailable: fr.native_point_available === true,
            kind: fr.kind || 'unknown',
            leadMin: Number.isFinite(Number(fr.lead_minutes)) ? Number(fr.lead_minutes) : null,
            url: fr.url ? new URL(fr.url, RADAR_API_BASE).toString() : null,
            tiles: fr.tiles || null,
            bounds: fr.bounds || null,
            palette: fr.palette || null,
            maxzoom: fr.maxzoom || 7,
            srcId: `ws-radar-src-${i}`,
            lyrId: `ws-radar-${i}`,
        }))
        .filter(fr => Number.isFinite(fr.timeMs));

    const signature = JSON.stringify([state.unit, state.activeProduct, frames]);
    const framesChanged = signature !== state.frameSignature;
    const previousClock = state.baseTimeMs;
    state.baseTimeMs = parseUtcMs(data.base_time_utc);

    if (framesChanged) {
        clearFrames();
        state.frames = frames;
        enforceObservationLayerOrder(map);
        state.frameSignature = signature;
    }

    const clockChanged = previousClock !== state.baseTimeMs;
    NowcastTimeline.setDataClock('radar', state.baseTimeMs);
    if (framesChanged && !clockChanged) NowcastTimeline.notifyDataUpdated();
}

function refreshFrames() {
    const generation = state.generation;
    if (state.refreshPromise && state.refreshGeneration === generation) return state.refreshPromise;
    const promise = loadFrames(generation).then(() => {
        if (generation !== state.generation) return;
        document.dispatchEvent(new CustomEvent('wsv2:radar-status', { detail: {
            available: state.sourceAvailable === true && state.frames.length > 0, product: state.product,
            unit: state.unit, activeProduct: state.activeProduct,
            warning: state.warning, baseTimeMs: state.baseTimeMs,
            forecastHorizonMinutes: state.forecastHorizon, forecastMethod: state.forecastMethod,
        } }));
    }).catch(error => {
        if (generation === state.generation) {
            document.dispatchEvent(new CustomEvent('wsv2:radar-status', { detail: {
                available: false, product: state.product,
                warning: 'Fonte temporaneamente non disponibile. Riprovo automaticamente.',
            } }));
        }
        throw error;
    }).finally(() => {
        if (state.refreshPromise === promise) state.refreshPromise = null;
    });
    state.refreshPromise = promise;
    state.refreshGeneration = generation;
    return promise;
}

function showAtTime(T) {
    if (!state.frames.length) { setUnipaRadarReference(null); return; }
    const last = state.frames[state.frames.length - 1];
    // Another live layer can be newer than DPC. At the live cursor retain the
    // latest observation, but never extrapolate it into a forecast position.
    const observed = [...state.frames].reverse().find(frame => ['observed', 'satellite_estimate'].includes(frame.kind));
    if (state.product !== 'rain' && T === NowcastTimeline.referenceTime()
        && observed && Date.now() - observed.timeMs <= (state.product === 'satellite-rain' ? 30 : 40) * 60000) T = observed.timeMs;
    if (T > last.timeMs + 2.5 * 60000) {
        if (state.currentIdx >= 0) {
            const previous = state.frames[state.currentIdx];
            if (state.map.getLayer(previous.lyrId)) state.map.setPaintProperty(previous.lyrId, 'raster-opacity', 0);
        }
        state.currentIdx = -1;
        setUnipaRadarReference(null);
        return;
    }
    // Frame più vicino ≤ T (se T precede tutto, il primo).
    let idx = 0;
    for (let i = 0; i < state.frames.length; i++) {
        if (state.frames[i].timeMs <= T) idx = i;
        else break;
    }
    const selected = state.frames[idx];
    setUnipaRadarReference(state.unit === 'dBZ' && selected.kind === 'observed' ? selected.timeMs : null);
    const palette = selected.palette || state.basePalette;
    if (palette && palette !== state.palette) { state.palette = palette; buildRevLut(palette); publishRadarLegend(); }
    if (idx === state.currentIdx) return;
    const map = state.map;
    if (state.currentIdx >= 0) {
        const prev = state.frames[state.currentIdx];
        if (map.getLayer(prev.lyrId)) map.setPaintProperty(prev.lyrId, 'raster-opacity', 0);
    }
    const cur = state.frames[idx];
    prepareVisibleFrames(idx);
    if (map.getLayer(cur.lyrId)) map.setPaintProperty(cur.lyrId, 'raster-opacity', OPACITY);
    state.currentIdx = idx;
}

export function initRadar(map, { beforeId } = {}) {
    state.map = map;
    state.beforeId = beforeId;
    state.weather3D = initWeather3D(map, () => {
        const frame = state.frames[state.currentIdx];
        return { enabled: state.enabled, frame, product: state.product, unit: state.unit, bounds: frame?.bounds || state.boundsLL, palette: frame?.palette || state.basePalette, publishLegend: () => { if(state.enabled) publishRadarLegend(); } };
    });
}

export async function setRadarEnabled(on) {
    // The local observation is subordinate to the displayed national frame.
    if (!on) setUnipaRadarReference(null);
    void setUnipaRadarEnabled(on && state.product !== 'satellite-rain');
    if (on === state.enabled) return;
    const generation = ++state.generation;
    state.enabled = on;
    if (on) {
        try { await refreshFrames(); } catch (e) {
            console.warn('[wsv2] radar:', e);
            // Keep the selected layer active so the minute/visibility timers
            // can recover after a transient provider or network failure.
        }
        if (!state.enabled || generation !== state.generation) return;
        NowcastTimeline.register('radar', showAtTime);
        state.map.on('click', onRadarClick);   // click→mm/h (solo con stazioni OFF)
        state.timer = setInterval(() => {
            if (!document.hidden && state.enabled) refreshFrames().catch(() => {});
        }, REFRESH_MS);
        state.visibilityHandler = () => {
            if (!document.hidden && state.enabled) refreshFrames().catch(() => {});
        };
        document.addEventListener('visibilitychange', state.visibilityHandler);
    } else {
        NowcastTimeline.unregister('radar');
        NowcastTimeline.clearDataClock('radar');
        NowcastTimeline.clearForecastHorizon?.('radar');
        state.map.off('click', onRadarClick);
        if (state._clickPopup) { state._clickPopup.remove(); state._clickPopup = null; }
        if (state.timer) clearInterval(state.timer);
        state.timer = null;
        if (state.visibilityHandler) document.removeEventListener('visibilitychange', state.visibilityHandler);
        state.visibilityHandler = null;
        clearFrames();
        setWorldRadarCoverage(null);
        state.baseTimeMs = null;
        document.dispatchEvent(new CustomEvent('wsv2:map-legend', { detail: { id: 'radar', spec: null } }));
    }
}


export async function setRadarProduct(product) {
    if (!['auto', 'sri-national', 'vmi-national', 'rain', 'satellite-rain'].includes(product) || product === state.product) return;
    const enabled = state.enabled;
    if (enabled) await setRadarEnabled(false);
    state.product = product;
    if (enabled) await setRadarEnabled(true);
}
